import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import WizardLayout from './wizard/WizardLayout';
import Step1LocationInfo from './wizard/Step1LocationInfo';
import Step3Question1 from './wizard/Step3Question1';
import Step3Question2 from './wizard/Step3Question2';
import Step3Question3 from './wizard/Step3Question3';
import Step3Question4 from './wizard/Step3Question4';
import Step3Question5 from './wizard/Step3Question5';
import Step3Question6 from './wizard/Step3Question6';
import Step3Question7 from './wizard/Step3Question7';
import Step3Question8 from './wizard/Step3Question8';
import Step3Question9 from './wizard/Step3Question9';
import Step4Review from './wizard/Step4Review';
import { CheckCircleIcon } from '@heroicons/react/24/outline';


interface QuestionAnswer {
  answer?: string;
  comments?: string;
  details?: string;
  actualStartTime?: string;
  invalidBallotCount?: number;
  explanation?: string;
}

interface ChecklistSection {
  [questionId: string]: QuestionAnswer;
}

interface WizardData {
  step1: {
    timeline: string;
    district: string;
    constituency: string;
    ward: string;
    votingCenter: string;
    observerName: string;
    observerContact: string;
    observerOrganization: string;
  };
  step3: {
    question1: ChecklistSection;
    question2: ChecklistSection;
    question3: ChecklistSection;
    question4: ChecklistSection;
    question5: ChecklistSection;
    question6: ChecklistSection;
    question7: ChecklistSection;
    question8: ChecklistSection;
    question9: ChecklistSection;
  };
}

interface ObserverDayWizardProps {
  onClose?: () => void;
}

const ObserverDayWizard: React.FC<ObserverDayWizardProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { token, user, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUserTransition, setIsUserTransition] = useState(false);
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [submittedReportId, setSubmittedReportId] = useState<string | null>(null);

  const STORAGE_KEY = React.useMemo(
    () => (user?.id ? `observerWizardData:${user.id}` : null),
    [user?.id]
  );



  const [wizardData, setWizardData] = useState<WizardData>({
    step1: {
      timeline: '',
      district: '',
      constituency: '',
      ward: '',
      votingCenter: '',
      observerName: '',
      observerContact: '',
      observerOrganization: ''
    },
    step3: {
      question1: {},
      question2: {},
      question3: {},
      question4: {},
      question5: {},
      question6: {},
      question7: {},
      question8: {},
      question9: {}
    }
  });

  // Function to reset wizard to initial state
  const resetWizardData = () => {
    const initialData: WizardData = {
      step1: {
        timeline: '',
        district: '',
        constituency: '',
        ward: '',
        votingCenter: '',
        observerName: '',
        observerContact: '',
        observerOrganization: ''
      },
      step3: {
        question1: {},
        question2: {},
        question3: {},
        question4: {},
        question5: {},
        question6: {},
        question7: {},
        question8: {},
        question9: {}
      }
    };

    setWizardData(initialData);
    setCurrentStep(1);
    setErrors({});

    // Clear localStorage for all users
    if (STORAGE_KEY) {
      localStorage.removeItem(STORAGE_KEY);
    }
    // Also clear the old general key for backwards compatibility
    localStorage.removeItem('observerWizardData');
  };


  // Auto-save to localStorage (per-user) - but not during user transitions
  useEffect(() => {
    if (!STORAGE_KEY || isUserTransition) return;
    const t = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(wizardData));
    }, 800);
    return () => clearTimeout(t);
  }, [wizardData, STORAGE_KEY, isUserTransition]);

  // Load saved data on user change
  useEffect(() => {
    console.log('User changed in ObserverDayWizard:', user ? `${user.name} (ID: ${user.id})` : 'null');
    console.log('STORAGE_KEY:', STORAGE_KEY);

    setIsUserTransition(true); // Prevent auto-save during transition

    if (!STORAGE_KEY || !user) {
      console.log('Clearing wizard data - no user or storage key');
      // If no storage key or no user, reset everything
      setWizardData({
        step1: {
          timeline: '',
          district: '',
          constituency: '',
          ward: '',
          votingCenter: '',
          observerName: '',
          observerContact: '',
          observerOrganization: '',
        },
        step3: {
          question1: {}, question2: {}, question3: {}, question4: {}, question5: {},
          question6: {}, question7: {}, question8: {}, question9: {}
        }
      });

      // Allow auto-save again after transition
      setTimeout(() => setIsUserTransition(false), 100);
      return;
    }

    const saved = localStorage.getItem(STORAGE_KEY);
    console.log('Saved data for user:', saved ? 'Found' : 'Not found');

    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        console.log('Loading saved data:', parsed.step1);
        setWizardData(parsed);

        // Allow auto-save again after transition
        setTimeout(() => setIsUserTransition(false), 100);
        return;
      } catch { }
    }

    // If no saved data for this user, create fresh data with user info
    console.log('Creating fresh data for user:', user.district, user.constituency, user.ward);
    setWizardData({
      step1: {
        timeline: '',
        district: user?.district || '',
        constituency: user?.constituency || '',
        ward: user?.ward || '',
        votingCenter: '',
        observerName: user?.name || '',
        observerContact: user?.phone || '',
        observerOrganization:
          (user?.organisation_name ?? user?.organization_name ?? user?.organisation?.name) || '',
      },
      step3: {
        question1: {}, question2: {}, question3: {}, question4: {}, question5: {},
        question6: {}, question7: {}, question8: {}, question9: {}
      }
    });

    // Allow auto-save again after transition
    setTimeout(() => setIsUserTransition(false), 100);
  }, [STORAGE_KEY, user]); // Only depend on STORAGE_KEY and user object, not individual properties
  // Enforce authentication for access to the wizard
  useEffect(() => {
    if (!token) {
      navigate('/admin/login', {
        state: {
          message: 'Please log in to access the Observer Day Form',
          redirectTo: '/admin/observer-checklist'
        }
      });
    }
  }, [token, navigate]);

  // Auto-save to localStorage
  useEffect(() => {
    const autoSave = setTimeout(() => {
      localStorage.setItem('observerWizardData', JSON.stringify(wizardData));
    }, 1000);
    return () => clearTimeout(autoSave);
  }, [wizardData]);

  // Load saved data on mount
  useEffect(() => {
    const savedData = localStorage.getItem('observerWizardData');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setWizardData(parsed);
      } catch (error) {
        console.error('Error loading saved data:', error);
      }
    }
  }, []);

  const validateStep1 = () => {
    const newErrors: Record<string, string> = {};
    const { step1 } = wizardData;

    // Timeline validation
    if (!step1.timeline) newErrors.timeline = 'Observation timeline is required';

    // Location validation (1-100 characters, required)
    if (!step1.district?.trim()) newErrors.district = 'District is required';
    else if (step1.district.length > 100) newErrors.district = 'District must be 1-100 characters';

    if (!step1.constituency?.trim()) newErrors.constituency = 'Constituency is required';
    else if (step1.constituency.length > 100) newErrors.constituency = 'Constituency must be 1-100 characters';

    if (!step1.ward?.trim()) newErrors.ward = 'Ward is required';
    else if (step1.ward.length > 100) newErrors.ward = 'Ward must be 1-100 characters';

    if (!step1.votingCenter?.trim()) newErrors.votingCenter = 'Voting center is required';
    else if (step1.votingCenter.length > 255) newErrors.votingCenter = 'Center name must be 1-255 characters';

    // Observer name validation (2-255 characters, required)
    if (!step1.observerName?.trim()) newErrors.observerName = 'Observer name is required';
    else if (step1.observerName.length < 2) newErrors.observerName = 'Name must be at least 2 characters';
    else if (step1.observerName.length > 255) newErrors.observerName = 'Name must be 2-255 characters';

    // Observer contact validation (8-20 characters, Malawi format: +265[7-9][0-9]{8})
    if (step1.observerContact?.trim()) {
      if (step1.observerContact.length < 8 || step1.observerContact.length > 20) {
        newErrors.observerContact = 'Contact must be 8-20 characters';
      } else if (!/^\+265[7-9][0-9]{8}$/.test(step1.observerContact)) {
        newErrors.observerContact = 'Please enter valid Malawi phone number (+265[7-9]xxxxxxxx)';
      }
    } // Contact is optional

    // Observer organization validation (2-255 characters, optional)
    if (step1.observerOrganization?.trim()) {
      if (step1.observerOrganization.length < 2) newErrors.observerOrganization = 'Organization must be at least 2 characters';
      else if (step1.observerOrganization.length > 255) newErrors.observerOrganization = 'Organization must be 2-255 characters';
    } // Organization is optional

    return newErrors;
  };


  const validateCurrentQuestionStep = () => {
    const newErrors: Record<string, string> = {};
    const { step3 } = wizardData;

    if (currentStep < 2 || currentStep > 10) return newErrors;

    const questionNumber = currentStep - 1; // Step 2 = Question 1, Step 3 = Question 2, etc.
    const questionKey = `question${questionNumber}`;
    const questionData = step3[questionKey as keyof typeof step3];

    // For now, basic validation - each question should have at least some answers
    if (questionData && Object.keys(questionData).length === 0) {
      newErrors[questionKey] = 'Please answer the questions in this section';
    }

    return newErrors;
  };

  const validateCurrentStep = () => {
    let newErrors: Record<string, string> = {};

    switch (currentStep) {
      case 1:
        newErrors = validateStep1();
        break;
      case 2:
      case 3:
      case 4:
      case 5:
      case 6:
      case 7:
      case 8:
      case 9:
      case 10:
        newErrors = validateCurrentQuestionStep();
        break;
      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canProceed = () => {
    const { step1, step3 } = wizardData;

    switch (currentStep) {
      case 1:
        // Required fields: timeline, district, constituency, ward, votingCenter, observerName
        // Optional fields: observerContact, observerOrganization
        const step1Valid = !!(step1.timeline && step1.district && step1.constituency &&
          step1.ward && step1.votingCenter && step1.observerName.trim());

        if (!step1Valid) {
          console.log('Step 1 validation failed:', {
            timeline: !!step1.timeline,
            district: !!step1.district,
            constituency: !!step1.constituency,
            ward: !!step1.ward,
            votingCenter: !!step1.votingCenter,
            observerName: !!step1.observerName.trim(),
            currentData: step1
          });
        }

        return step1Valid;
      case 2: // Question 1
        return Object.keys(step3.question1 || {}).length > 0;
      case 3: // Question 2  
        return Object.keys(step3.question2 || {}).length > 0;
      case 4: // Question 3
        return Object.keys(step3.question3 || {}).length > 0;
      case 5: // Question 4
        return Object.keys(step3.question4 || {}).length > 0;
      case 6: // Question 5
        return Object.keys(step3.question5 || {}).length > 0;
      case 7: // Question 6
        return Object.keys(step3.question6 || {}).length > 0;
      case 8: // Question 7
        return Object.keys(step3.question7 || {}).length > 0;
      case 9: // Question 8
        return Object.keys(step3.question8 || {}).length > 0;
      case 10: // Question 9
        return Object.keys(step3.question9 || {}).length > 0;
      case 11: // Review & Submit
        return true;
      default:
        return false;
    }
  };

  // Helper function to get the next valid step based on timeline
  const getNextStep = (currentStep: number, timeline: string): number => {
    if (timeline === 'vote_count') {
      // For vote count timeline, only show steps 1, 2, 3, then jump to 11 (review)
      if (currentStep === 1) return 2;
      if (currentStep === 2) return 3;
      if (currentStep === 3) return 4;
      if (currentStep === 4) return 11; // Skip to review
      return currentStep + 1;
    }
    // For other timelines, normal progression
    return currentStep + 1;
  };

  // Helper function to get the previous valid step based on timeline
  const getPreviousStep = (currentStep: number, timeline: string): number => {
    if (timeline === 'vote_count') {
      // For vote count timeline, only show steps 1, 2, 3, then jump to 11 (review)
      if (currentStep === 11) return 4; // From review, go back to step 4
      if (currentStep === 4) return 3;
      if (currentStep === 3) return 2;
      if (currentStep === 2) return 1;
      return currentStep - 1;
    }
    // For other timelines, normal progression
    return currentStep - 1;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      const nextStep = getNextStep(currentStep, wizardData.step1.timeline);
      if (nextStep <= 11) {
        setCurrentStep(nextStep);
      } else {
        handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    const prevStep = getPreviousStep(currentStep, wizardData.step1.timeline);
    if (prevStep >= 1) {
      setCurrentStep(prevStep);
      setErrors({});
    }
  };

  // Helper function to check if a step is valid for the current timeline
  const isValidStepForTimeline = (step: number, timeline: string): boolean => {
    if (timeline === 'vote_count') {
      // For vote count timeline, only allow steps 1, 2, 3, 4, and 11
      return [1, 2, 3, 4, 11].includes(step);
    }
    // For other timelines, all steps are valid
    return step >= 1 && step <= 11;
  };

  const handleStepClick = (step: number) => {
    // Check if step is valid for current timeline
    if (!isValidStepForTimeline(step, wizardData.step1.timeline)) {
      return;
    }

    // Validate current step before allowing navigation
    if (step < currentStep || validateCurrentStep()) {
      setCurrentStep(step);
    }
  };

  // Helper function to convert question ID to user-friendly question number
  const getQuestionDisplayName = (questionId: string): string => {
    // Remove any suffixes like _parties
    const baseId = questionId.replace('_parties', '');

    // Extract the question number from the ID (e.g., "1a" -> "1a", "22g" -> "22g")
    if (baseId.match(/^\d+[a-z]?$/)) {
      return `Question ${baseId.toUpperCase()}`;
    }

    // Fallback for other formats
    return `Question ${baseId}`;
  };

  // Helper function to format validation errors for users
  const formatValidationErrors = (errorData: any): string | null => {
    if (errorData.invalid_responses && Array.isArray(errorData.invalid_responses)) {
      const errorMessages = errorData.invalid_responses.map((invalidResponse: any) => {
        const questionName = getQuestionDisplayName(invalidResponse.question_id);

        // Provide specific error messages based on question type
        let specificError = '';
        switch (invalidResponse.question_type) {
          case 'yesno':
            specificError = 'Please select Yes or No';
            break;
          case 'number':
            specificError = 'Please enter a valid number';
            break;
          case 'text':
            specificError = 'Please provide a valid text response';
            break;
          case 'time':
            specificError = 'Please enter a valid time (HH:MM format)';
            break;
          case 'yes_no_explanation':
            specificError = 'Please provide both a Yes/No answer and an explanation';
            break;
          case 'yes_no_time_duration':
            specificError = 'Please provide both a Yes/No answer and time duration';
            break;
          case 'politicalparty':
            specificError = 'Please provide valid political party information';
            break;
          default:
            specificError = 'Please check your answer format';
        }

        return `${questionName}: ${specificError}`;
      });

      if (errorMessages.length === 1) {
        return errorMessages[0];
      } else {
        return `Please fix the following issues:\n${errorMessages.join('\n')}`;
      }
    }

    return null;
  };

  const handleSubmit = async () => {
    if (!validateCurrentStep()) return;

    setIsSubmitting(true);

    try {
      // Question text mapping - consolidated from all Step3Question files
      const QUESTION_TEXT_MAPPING: Record<string, string> = {
        // Morning questions
        '1a': 'Did the Polling Centre open on time?',
        '1b': 'Was there a long voting queue/line when the Polling Station opened?',
        '2a': 'Total number of Electoral Commission Officials present',
        '2b': 'Total number of security personnel present',
        '2c': 'Are there Official Political Party Representatives present?',
        '3a': 'Security personnel have been able to maintain order at the Polling Station',
        '3b': 'The location of the Polling Station was in a safe and convenient location for the women.',

        // Afternoon questions
        '10a': 'Was the polling station still open?',
        '10b': 'Was there a long voting queue/line in the afternoon hours?',
        '11a': 'Total number of Electoral Commission Officials present',
        '11b': 'Total number of security personnel present',
        '11c': 'Are there Official Political Party Representatives present?',
        '12a': 'Security personnel have been able to maintain order at the Polling Station',
        '12b': 'The location of the Polling Station was in a safe and convenient location for the women.',
        '13a': 'Number of additional ballot boxes received at polling station',
        '13b': 'Number of embossing devices at Polling Station',
        '13c': 'Number of Ink bottles for marking fingers at Polling Station',
        '14a': 'Have you witnessed or heard of any malfunctions of polling equipment or shortage of voting materials that caused voting to be negatively affected? How long did it take to resolve the issue?',
        '15a': 'Voters were asked to show their Voter Registration Certificates before voting?',
        '15b': 'Voters without Voter Registration Certificates were not allowed to vote.',
        '15c': 'Voters with Voter Registration Certificate from other Polling Stations were allowed to vote.',
        '15d': 'Voters names were ticked off the Voter Register/List once they were identified?',
        '15e': 'Voters names were called out loud enough for all monitors to hear?',
        '15f': 'How many people came without voter certificates?',
        '15g': 'How many people were sent back from the polling station?',
        '16a': 'The voting procedures were explained to voters by MEC Officials.',
        '16b': 'Voters were given privacy to cast their ballots after pointing out the correct boxes to them.',
        '16c': 'Voters Registration Certificate cards were embossed after voting?',
        '16d': 'Number of people attempting to vote twice?',
        '16e': 'Voters who made mistakes were given new ballot papers',
        '16f': 'Voters surrendered spoilt ballot papers after requesting new ones',
        '16g': 'Were voters index fingers checked for ink before voting',
        '16h': 'Were voters index fingers inked on voting',
        '17a': 'The voting queue/line was moving along during the Afternoon hours (10:00am to 02:00pm)?',
        '17b': 'The Polling Station was spacious and adequate for Voters\' movement and voting process.',
        '17c': 'The polling station was shaded and protected from the sun and rain.',
        '17d': 'The polling station implemented measures with respect to protecting voters from any health outbreaks at the time',
        '18a': 'Persons with disabilities were given priority & easy access to vote',
        '18b': 'Pregnant women were given priority and easy access to vote.',
        '18c': 'Women with children under 5 were given priority and easy access to vote',
        '18d': 'The elderly were given priority and easy access to vote?',
        '19a': 'Party observers were able to fully observe the voting process and perform their duties',
        '19b': 'Various CSO observers were able to fully observe the voting process and perform their duties',
        '19c': 'Did you witness or hear of any complaints lodged at the polling station by observers and or anyone else',
        '19c1': 'If yes to 19c: How many complaints were lodged?',
        '19d': 'Have you witnessed or heard any persons or people who have been ferried from another area to vote at the Centre you observed?',

        // Vote count questions
        '20a': 'What time did the Polling Centre close?',
        '21a': 'Were ballot papers counted at the Polling Centre?',
        '21b': 'Was there adequate light for ballot counting at the Polling Centre?',
        '22a': 'The ballot papers were transported somewhere else for counting?',
        '22b': 'Number of unused ballot papers at the Polling Centre',
        '22c': 'Number of spoilt ballot papers at the Polling Centre',
        '22d': 'Number of null and void votes at the Polling Centre',
        '22e': 'Were party representatives present at vote counting?',
        '22f': 'Were observers present and able to perform their duty at vote counting?',
        '22g': 'You were able to capture the data of votes counted and submitted on Maso Athu',
        '22h': 'Are the school facilities (furniture, toilets, etc..) used during elections left as they were at the beginning',


        // Additional morning/afternoon questions
        '4a': 'The ballot paper packages were sealed and was opened in the presence of all Officials',
        '4b': 'Number of ballot papers on opening station',
        '4c': 'Number of ballot boxes on opening station',
        '4d': 'Number of embossing devices on opening station',
        '4e': 'Number of Ink bottles for marking fingers on opening station',
        '4f': 'Number of pens on opening station',
        '4g': 'How many Voters List/Register were available at the Polling Station at the time of opening?',
        '4h': 'Have you witnessed or heard of any malfunctions of polling equipment or shortage of voting materials that caused voting to be negatively affected? How long did it take to resolve the issue?',
        '5a': 'Voters were asked to show their Voter Registration Certificates before voting?',
        '5b': 'Voters without Voter Registration Certificates were not allowed to vote.',
        '5c': 'Voters with Voter Registration Certificate from other Polling Stations were allowed to vote.',
        '5d': 'Voters names were ticked off the Voter Register/List once they were identified?',
        '5e': 'Voters names were called out loud enough for all monitors to hear?',
        '5f': 'How many people came without voter certificates?',
        '5g': 'How many people were sent back from the polling station?',
        '6a': 'The voting procedures were explained to voters by MEC Officials.',
        '6b': 'Voters were given privacy to cast their ballots after pointing out the correct boxes to them.',
        '6c': 'Party representatives were campaigning at or near Polling Station',
        '6d': 'Party representatives were soliciting to buy votes at Polling Station location.',
        '6e': 'Voters Registration Certificate cards were embossed after voting?',
        '6f': 'Number of people attempting to vote twice?',
        '6g': 'Voters who made mistakes were given new ballot papers',
        '6h': 'Voters surrendered spoilt ballot papers after requesting new ones',
        '6i': 'Voters index fingers were checked for ink before voting',
        '6j': 'Voters index fingers inked on voting',
        '7a': 'The voting queue/line was moving along during the Morning hours (6:00am to 10:00am)?',
        '7b': 'The Polling Station was spacious and adequate for Voters\' movement and voting process.',
        '7c': 'The polling station was shaded and protected from the sun and rain.',
        '7d': 'The polling station implemented measures with respect to protecting voters from any health outbreaks at the time',
        '8a': 'Persons with disabilities were given priority & easy access to vote',
        '8b': 'Pregnant women were given priority and easy access to vote.',
        '8c': 'Women with children under 5 were given priority and easy access to vote',
        '8d': 'The elderly were given priority and easy access to vote?',
        '9a': 'Party observers were able to fully observe the voting process and perform their duties',
        '9b': 'Various CSO observers were able to fully observe the voting process and perform their duties',
        '9c': 'Did you witness or hear of any complaints lodged at the polling station by observers and or anyone else',
        '9d': 'How many complaints were lodged',

        // Political party questions
        '2c_parties': 'Political party representatives present',
        '11c_parties': 'Political party representatives present',
        '22c_parties': 'Political party representatives present',
        '22e_parties': 'Party representatives present at vote counting',
      };

      // Helper function to convert our data to API responses format
      const convertToAPIResponses = () => {
        const responses: any[] = [];
        const timeline = wizardData.step1.timeline;

        const isQuestionRelevantForTimeline = (questionId: string, timeline: string): boolean => {
          if (timeline === 'vote_count') {
            return questionId.startsWith('20') || questionId.startsWith('21') || questionId.startsWith('22');
          }
          return !questionId.startsWith('20') && !questionId.startsWith('21') && !questionId.startsWith('22');
        };

        Object.entries(wizardData.step3).forEach(([questionSection, questionData]) => {
          Object.entries(questionData as Record<string, any>).forEach(([questionId, answerData]) => {
            if (answerData && typeof answerData === 'object' && (answerData.answer !== undefined || answerData.genderMale !== undefined || answerData.genderFemale !== undefined)) {

              if (!isQuestionRelevantForTimeline(questionId, timeline)) {
                return;
              }

              // Skip the special _parties processing here (unchanged)
              if (questionId.includes('_parties')) {
                // First, send the Yes/No response for the base question
                const baseQuestionId = questionId.replace('_parties', '');
                const baseQuestionData = (questionData as Record<string, any>)[baseQuestionId];

                // Check if we already processed the base question to avoid duplicates
                const alreadyProcessed = responses.some(r => r.question_id === baseQuestionId);

                if (baseQuestionData && baseQuestionData.answer && !alreadyProcessed) {
                  responses.push({
                    question_id: baseQuestionId,
                    question_text: QUESTION_TEXT_MAPPING[baseQuestionId] || `Question ${baseQuestionId}`,
                    response_value: baseQuestionData.answer,
                    question_type: (baseQuestionId === '6f' || baseQuestionId === '16d') ? 'text' : 'yesno'
                  });
                }

                // Then, if the answer was "Yes", send the political party data with gender breakdown
                if (baseQuestionData?.answer === 'Yes') {
                  try {
                    const partyData = JSON.parse(answerData.details || '[]');
                    // Filter out entries without a partyName
                    const validParties = partyData.filter((party: any) => party && party.partyName && String(party.partyName).trim() !== '');

                    if (validParties.length > 0) {
                      const partyResponse = {
                        question_id: questionId,
                        question_text: QUESTION_TEXT_MAPPING[questionId] || `Question ${questionId}`,
                        response_value: '',
                        question_type: 'politicalparty',
                        response_data: validParties.map((party: any) => ({
                          id: party.id,
                          partyName: party.partyName,
                          maleRepresentatives: Number(party.maleRepresentatives || 0),
                          femaleRepresentatives: Number(party.femaleRepresentatives || 0),
                          totalRepresentatives: Number(party.maleRepresentatives || 0) + Number(party.femaleRepresentatives || 0),
                        }))
                      };
                      responses.push(partyResponse);
                    }
                  } catch (error) {
                    console.error(`Error parsing party data for ${questionId}:`, error, answerData);
                  }
                }
                return; // Skip the normal processing for _parties questions
              }

              // Yes/No + Gender breakdown questions first (2c, 6f, 16d)
              if ((questionId === '6f' || questionId === '16d')) {
                if (answerData.answer === 'Yes') {
                  const male = Number(answerData.genderMale || 0);
                  const female = Number(answerData.genderFemale || 0);
                  const total = male + female;
                  responses.push({
                    question_id: questionId,
                    question_text: QUESTION_TEXT_MAPPING[questionId] || `Question ${questionId}`,
                    response_value: 'Yes',
                    question_type: 'text',
                    response_data: { male, female, total }
                  });
                } else if (answerData.answer === 'No') {
                  responses.push({
                    question_id: questionId,
                    question_text: QUESTION_TEXT_MAPPING[questionId] || `Question ${questionId}`,
                    response_value: 'No',
                    question_type: 'yesno'
                  });
                }
                return;
              }

              // Gender-only questions (2a,2b,5f,5g,11a,11b,15f,15g)
              if (answerData.genderMale !== undefined || answerData.genderFemale !== undefined) {
                const male = Number(answerData.genderMale || 0);
                const female = Number(answerData.genderFemale || 0);
                const total = male + female;

                responses.push({
                  question_id: questionId,
                  question_text: QUESTION_TEXT_MAPPING[questionId] || `Question ${questionId}`,
                  response_value: String(total),
                  question_type: 'text',
                  response_data: { male, female, total }
                });
                return;
              }

              const response: any = {
                question_id: questionId,
                question_text: QUESTION_TEXT_MAPPING[questionId] || `Question ${questionId}`,
                response_value: answerData.answer
              };

              // Special handling for question 1a (unchanged)
              if (questionId === '1a' && answerData.answer === 'No') {
                response.question_type = 'yes_no_explanation';
                try {
                  const selectedReasons = answerData.details ? JSON.parse(answerData.details) : [];
                  const otherExplanation = selectedReasons.includes('Other') ? answerData.comments : null;

                  // Create explanation text for backend validation
                  let explanationText = selectedReasons.join(', ');
                  if (otherExplanation) {
                    explanationText += ` - Other: ${otherExplanation}`;
                  }

                  response.response_data = {
                    explanation: explanationText, // Required by backend validation
                    reasons: selectedReasons,
                    other_explanation: otherExplanation
                  };
                } catch {
                  response.response_data = {
                    explanation: 'No reasons provided', // Required by backend validation
                    reasons: [],
                    other_explanation: null
                  };
                }
              }
              // Vote count time pattern (unchanged)
              else if (questionId.endsWith('a') && (questionId.startsWith('20') || questionId.startsWith('21') || questionId.startsWith('22'))) {
                response.question_type = answerData.answer?.includes(':') ? 'time' : 'yesno';
              } else if (answerData.details) {
                response.question_type = 'yes_no_explanation';
                response.response_data = { explanation: answerData.details };
              } else if (answerData.answer === 'Yes' || answerData.answer === 'No') {
                response.question_type = 'yesno';
              } else if (!isNaN(Number(answerData.answer))) {
                response.question_type = 'number';
              } else {
                response.question_type = 'text';
              }

              responses.push(response);
            }
          });
        });

        console.log(`Timeline: ${timeline}, Relevant responses: ${responses.length}`);
        return responses;
      };

      // Create submission payload according to backend API spec
      const payload = {
        report_id: `obs_${Date.now()}`,
        observer_name: wizardData.step1.observerName,
        observer_contact: wizardData.step1.observerContact || undefined,
        observer_organization: wizardData.step1.observerOrganization || undefined,
        district: wizardData.step1.district,
        constituency: wizardData.step1.constituency,
        ward: wizardData.step1.ward,
        center: wizardData.step1.votingCenter,
        timeline: wizardData.step1.timeline,
        submission_method: 'web_wizard',
        location_lat: undefined, // TODO: Add GPS coordinates if available
        location_lng: undefined,
        responses: convertToAPIResponses()
      };

      // Detailed logging of all question responses and authenticated user
      console.log('=== SUBMISSION DATA ===');
      console.log('Authenticated User:', user);
      console.log('Auth Token:', token ? 'Valid token exists' : 'No token available');
      console.log('Observer Information:', {
        name: wizardData.step1.observerName,
        contact: wizardData.step1.observerContact,
        organization: wizardData.step1.observerOrganization
      });
      console.log('Location Information:', {
        district: wizardData.step1.district,
        constituency: wizardData.step1.constituency,
        ward: wizardData.step1.ward,
        votingCenter: wizardData.step1.votingCenter,
        timeline: wizardData.step1.timeline
      });
      console.log('All Question Responses:', payload.responses);
      console.log('Raw Wizard Data:', wizardData);
      console.log('Submitting observer report:', payload);
      console.log('Number of responses:', payload.responses.length);
      console.log('=== END SUBMISSION DATA ===');

      // Basic validation before submission
      if (payload.responses.length === 0) {
        throw new Error('No responses to submit. Please complete at least one question.');
      }

      // Only warn if we exceed the backend limit (this should never happen with proper filtering)
      if (payload.responses.length > 100) {
        console.warn(`Large number of responses: ${payload.responses.length}`);
      }

      // Ensure authenticated
      if (!token) {
        throw new Error('You must be logged in to submit reports');
      }

      // Make actual API call to submit the report (auth required)
      // Use centralized axios client to ensure Authorization + baseURL consistency
      try {
        const axiosResp = await api.reports.create(payload);
        const result = axiosResp.data;
        console.log('Submission successful:', result);
      } catch (axiosErr: any) {
        // Normalize axios error to previous handling style
        const status = axiosErr?.response?.status;
        const data = axiosErr?.response?.data;
        if (data) {
          console.log('Error data:', data);

          // Try to format validation errors in a user-friendly way
          const userFriendlyError = formatValidationErrors(data);
          if (userFriendlyError) {
            throw new Error(userFriendlyError);
          }

          let errorMessage = 'Failed to submit report';
          if (typeof data?.details === 'string') {
            errorMessage += ': ' + data.details;
          } else if (Array.isArray(data?.details)) {
            errorMessage += ': ' + data.details.join(', ');
          } else if (data?.error) {
            errorMessage += ': ' + data.error;
          } else if (data?.message) {
            errorMessage += ': ' + data.message;
          } else if (status === 401) {
            errorMessage += ': Not authorized to access this route';
          }
          throw new Error(errorMessage);
        }
        // Fallback generic error
        throw new Error('Failed to submit report');
      }

      // Show success message
      toast.success('Observer report submitted successfully!');

      // Reset the entire wizard form to initial state
      resetWizardData();

      // Show success confirmation popup; navigate after user confirms
      setSubmittedReportId(payload.report_id);
      setSuccessMessage('Observer report submitted successfully!');
      setShowSuccessModal(true);

    } catch (error: any) {
      console.error('Submission error:', error);

      // Show user-friendly error message
      if (error instanceof Error) {
        // Handle multi-line error messages
        if (error.message.includes('\n')) {
          // For multi-line messages, show them in a more formatted way
          const lines = error.message.split('\n');
          toast.error(
            <div className="text-sm">
              {lines.map((line, index) => (
                <div key={index} className={index === 0 ? 'font-semibold mb-1' : 'ml-2'}>
                  {line}
                </div>
              ))}
            </div>,
            {
              duration: 8000, // Show longer for multiple errors
              style: {
                maxWidth: '500px',
              },
            }
          );
        } else {
          toast.error(error.message, {
            duration: 6000,
          });
        }
      } else {
        toast.error('Failed to submit report. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateStep1 = (data: WizardData['step1']) => {
    setWizardData(prev => ({ ...prev, step1: data }));
  };


  const updateStep3 = (data: WizardData['step3']) => {
    setWizardData(prev => ({ ...prev, step3: data }));
  };

  const updateQuestion1 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question1: data } }));
  };

  const updateQuestion2 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question2: data } }));
  };

  const updateQuestion3 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question3: data } }));
  };

  const updateQuestion4 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question4: data } }));
  };

  const updateQuestion5 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question5: data } }));
  };

  const updateQuestion6 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question6: data } }));
  };

  const updateQuestion7 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question7: data } }));
  };

  const updateQuestion8 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question8: data } }));
  };

  const updateQuestion9 = (data: ChecklistSection) => {
    setWizardData(prev => ({ ...prev, step3: { ...prev.step3, question9: data } }));
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1LocationInfo
            data={wizardData.step1}
            onChange={updateStep1}
            errors={errors}
          />
        );
      case 2:
        return (
          <Step3Question1
            data={wizardData.step3.question1}
            onChange={updateQuestion1}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={2}
            questionTitle="Polling Station Setup"
            questionDescription="This section covers the setup and opening procedures at the polling station including materials, accessibility, and starting conditions."
            currentSection={2}
            totalSections={11}
          />
        );
      case 3:
        return (
          <Step3Question2
            data={wizardData.step3.question2}
            onChange={updateQuestion2}
            errors={errors}
            timeline={wizardData.step1.timeline}
            questionNumber={3}
            questionTitle="Personnel Present"
            questionDescription="This section covers personnel present at the polling station including electoral commission officials, security personnel, and political party representatives."
            currentSection={3}
            totalSections={11}
          />
        );
      case 4:
        return (
          <Step3Question3
            data={wizardData.step3.question3}
            onChange={updateQuestion3}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={4}
            questionTitle="Ballot Box Security"
            questionDescription="This section covers the security measures for ballot boxes including seals, positioning, and handling procedures."
            currentSection={4}
            totalSections={11}
          />
        );
      case 5:
        return (
          <Step3Question4
            data={wizardData.step3.question4}
            onChange={updateQuestion4}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={5}
            questionTitle="Queue Management"
            questionDescription="This section covers the management of voter queues including organization, priority voters, and waiting times."
            currentSection={5}
            totalSections={11}
          />
        );
      case 6:
        return (
          <Step3Question5
            data={wizardData.step3.question5}
            onChange={updateQuestion5}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={6}
            questionTitle="Voter Registration and Verification"
            questionDescription="This section covers the voter registration and verification process including certificate checking, voter register handling, and voter identification."
            currentSection={6}
            totalSections={11}
          />
        );
      case 7:
        return (
          <Step3Question6
            data={wizardData.step3.question6}
            onChange={updateQuestion6}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={7}
            questionTitle="Voting Process"
            questionDescription="This section covers the voting process including voter privacy, ballot issuance, and the handling of ballot papers."
            currentSection={7}
            totalSections={11}
          />
        );
      case 8:
        return (
          <Step3Question7
            data={wizardData.step3.question7}
            onChange={updateQuestion7}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={8}
            questionTitle="Voting Environment"
            questionDescription="This section covers the voting environment including polling station security, voter assistance, and polling station management."
            currentSection={8}
            totalSections={11}
          />
        );
      case 9:
        return (
          <Step3Question8
            data={wizardData.step3.question8}
            onChange={updateQuestion8}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={9}
            questionTitle="Complaints and Incidents"
            questionDescription="This section covers any complaints or incidents that occurred during the voting process including how they were handled."
            currentSection={9}
            totalSections={11}
          />
        );
      case 10:
        return (
          <Step3Question9
            data={wizardData.step3.question9}
            onChange={updateQuestion9}
            errors={errors}
            selectedTimeline={wizardData.step1.timeline}
            questionNumber={10}
            questionTitle="Vote Counting"
            questionDescription="This section covers the vote counting process including procedures, transparency, and result recording."
            currentSection={10}
            totalSections={11}
          />
        );
      case 11:
        return (
          <Step4Review
            data={wizardData}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="max-w-4xl p-6 mx-auto">
        <div className="text-center">
          <div className="w-8 h-8 mx-auto mb-4 border-b-2 border-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!token) {
    return null;
  }
  return (
    <WizardLayout
      currentStep={currentStep}
      totalSteps={11}
      onNext={handleNext}
      onPrevious={handlePrevious}
      onStepClick={handleStepClick}
      canProceed={canProceed()}
      isLastStep={currentStep === 11}
      isSubmitting={isSubmitting}
      timeline={wizardData.step1.timeline}
      onClose={onClose}
    >
      {renderCurrentStep()}

      {/* Success Confirmation Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/50" />
          <div className="relative w-full max-w-md p-6 bg-white border border-gray-200 shadow-xl rounded-2xl">
            <div className="flex items-start">
              <div className="p-2 mr-3 bg-green-100 rounded-lg">
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Success</h3>
                <p className="mt-1 text-sm text-gray-600">{successMessage || 'Your report has been sent successfully.'}</p>
                {submittedReportId && (
                  <p className="mt-2 text-xs text-gray-500">Reference ID: <span className="font-mono">{submittedReportId}</span></p>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button
                className="btn btn-secondary"
                onClick={() => setShowSuccessModal(false)}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowSuccessModal(false);
                  if (user?.role === 'observer') {
                    if (onClose) {
                      onClose();
                    } else {
                      navigate('/admin/dashboard');
                    }
                  } else {
                    navigate('/admin/reports');
                  }
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </WizardLayout>
  );
};

export default ObserverDayWizard;
